import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const FeaturesPage = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  // Transform scroll progress to control motion graphic opacity
  const graphicOpacity = useTransform(scrollYProgress, [0, 0.05, 0.6, 0.8], [0, 1, 1, 0]);
  
  // Timeline scroll progress - each item appears at different scroll points
  const timelineProgress = useTransform(scrollYProgress, [0, 0.6], [0, 4]);
  

  return (
    <section ref={containerRef} className="relative min-h-screen lg:min-h-[400vh] bg-white">
      {/* Fixed Motion Graphic on the Right - Only visible when ProductPage is in view */}
      <motion.div 
        style={{ opacity: graphicOpacity }}
        className="fixed right-0 top-1/2 -translate-y-1/2 w-1/2 h-screen flex items-center justify-center z-10 hidden lg:flex"
      >
        <div className="relative w-[500px] h-[500px]">
          {/* Outer Rotating Ring */}
        <motion.div
          animate={{
            rotate: 360,
          }}
          transition={{
              duration: 40,
            repeat: Infinity,
            ease: "linear"
          }}
            className="absolute inset-0 flex items-center justify-center"
          >
            <svg width="500" height="500" viewBox="0 0 200 200" className="w-full h-full">
              <defs>
                <linearGradient id="circleGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.8" />
                  <stop offset="50%" stopColor="#8b5cf6" stopOpacity="0.5" />
                  <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.8" />
                </linearGradient>
              </defs>
              {/* Large Outer Circle */}
              <circle 
                cx="100" 
                cy="100" 
                r="85" 
                fill="none" 
                stroke="url(#circleGrad1)" 
                strokeWidth="2"
                opacity="0.7"
              />
              {/* Orbiting Points */}
              {Array.from({ length: 12 }).map((_, i) => {
                const angle = (i * 30) * (Math.PI / 180);
                const radius = 85;
                const x = 100 + radius * Math.cos(angle);
                const y = 100 + radius * Math.sin(angle);
                return (
            <motion.circle 
                    key={i}
                    cx={x}
                    cy={y}
                    r="3"
                    fill="url(#circleGrad1)"
              animate={{
                scale: [1, 1.2, 1],
                      opacity: [0.7, 0.9, 0.7]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
                      delay: i * 0.2
                    }}
                  />
                );
              })}
            </svg>
          </motion.div>

          {/* Middle Rotating Ring - Counter Rotation */}
          <motion.div
              animate={{
              rotate: -360,
              }}
              transition={{
              duration: 30,
                repeat: Infinity,
              ease: "linear"
            }}
            className="absolute inset-0 flex items-center justify-center"
          >
            <svg width="500" height="500" viewBox="0 0 200 200" className="w-full h-full">
              <defs>
                <linearGradient id="circleGrad2" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.6" />
                  <stop offset="50%" stopColor="#06b6d4" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.6" />
                </linearGradient>
              </defs>
              {/* Medium Circle */}
              <circle 
                cx="100" 
                cy="100" 
                r="60" 
                fill="none" 
                stroke="url(#circleGrad2)" 
                strokeWidth="1.5"
                opacity="0.7"
              />
              {/* Connecting Radial Lines */}
              {Array.from({ length: 8 }).map((_, i) => {
                const angle = (i * 45) * (Math.PI / 180);
                const x = 100 + 60 * Math.cos(angle);
                const y = 100 + 60 * Math.sin(angle);
                return (
                  <motion.line
                    key={i}
                    x1="100"
                    y1="100"
                    x2={x}
                    y2={y}
                    stroke="url(#circleGrad2)"
                    strokeWidth="1"
              opacity="0.4"
              animate={{
                      opacity: [0.35, 0.5, 0.35],
                      strokeWidth: [0.8, 1.2, 0.8]
              }}
              transition={{
                      duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
                      delay: i * 0.4
                    }}
                  />
                );
              })}
              {/* Orbiting Points */}
              {Array.from({ length: 8 }).map((_, i) => {
                const angle = (i * 45) * (Math.PI / 180);
                const radius = 60;
                const x = 100 + radius * Math.cos(angle);
                const y = 100 + radius * Math.sin(angle);
                return (
            <motion.circle 
                    key={i}
                    cx={x}
                    cy={y}
                    r="2.5"
                    fill="url(#circleGrad2)"
              animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.75, 0.9, 0.75]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
                      delay: i * 0.3
                    }}
                  />
                );
              })}
            </svg>
          </motion.div>

          {/* Inner Rotating Ring */}
          <motion.div
              animate={{
              rotate: 360,
              }}
              transition={{
              duration: 25,
                repeat: Infinity,
              ease: "linear"
            }}
            className="absolute inset-0 flex items-center justify-center"
          >
            <svg width="500" height="500" viewBox="0 0 200 200" className="w-full h-full">
              <defs>
                <radialGradient id="centerGrad" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#ffffff" stopOpacity="0.95" />
                  <stop offset="40%" stopColor="#3b82f6" stopOpacity="0.8" />
                  <stop offset="70%" stopColor="#8b5cf6" stopOpacity="0.6" />
                  <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.4" />
                </radialGradient>
              </defs>
              {/* Inner Circle */}
              <circle 
                cx="100" 
                cy="100" 
                r="35" 
                fill="none" 
              stroke="#06b6d4" 
                strokeWidth="2"
                opacity="0.8"
              />
              {/* Inner Orbiting Points */}
              {Array.from({ length: 6 }).map((_, i) => {
                const angle = (i * 60) * (Math.PI / 180);
                const radius = 35;
                const x = 100 + radius * Math.cos(angle);
                const y = 100 + radius * Math.sin(angle);
                return (
                  <motion.circle
                    key={i}
                    cx={x}
                    cy={y}
                    r="2"
                    fill="#06b6d4"
              animate={{
                      scale: [1, 1.15, 1],
                      opacity: [0.85, 0.95, 0.85]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
                      delay: i * 0.4
                    }}
                  />
                );
              })}
            </svg>
          </motion.div>

          {/* Central Core with Glow */}
          <motion.div
            className="absolute inset-0 flex items-center justify-center z-20"
              animate={{
              scale: [1, 1.08, 1],
              opacity: [0.92, 0.98, 0.92]
              }}
              transition={{
              duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
          >
            <svg width="500" height="500" viewBox="0 0 200 200" className="w-full h-full">
              <defs>
                <radialGradient id="coreGrad" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
                  <stop offset="30%" stopColor="#3b82f6" stopOpacity="0.95" />
                  <stop offset="60%" stopColor="#8b5cf6" stopOpacity="0.85" />
                  <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.7" />
                </radialGradient>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              {/* Outer Glow Circle */}
              <circle 
                cx="100" 
                cy="100" 
                r="45" 
                fill="url(#coreGrad)" 
                opacity="0.8"
                filter="url(#glow)"
              />
              {/* Main Circle */}
              <circle 
                cx="100" 
                cy="100" 
                r="35" 
                fill="url(#coreGrad)" 
                opacity="0.95"
              />
              {/* Inner White Core */}
              <circle 
                cx="100" 
                cy="100" 
                r="20" 
                fill="#ffffff" 
                opacity="0.98"
              />
              {/* Center Dot */}
              <circle 
                cx="100" 
                cy="100" 
              r="8"
                fill="#3b82f6" 
                opacity="0.9"
              />
            </svg>
          </motion.div>

          {/* Floating Particle Ring */}
          <div className="absolute inset-0 flex items-center justify-center">
            <svg width="500" height="500" viewBox="0 0 200 200" className="w-full h-full">
              {Array.from({ length: 16 }).map((_, i) => {
                const angle = (i * 22.5) * (Math.PI / 180);
                const radius = 72;
                const x = 100 + radius * Math.cos(angle);
                const y = 100 + radius * Math.sin(angle);
                return (
            <motion.circle
                    key={i}
                    cx={x}
                    cy={y}
                    r="1.5"
                    fill="#06b6d4"
                    initial={{ opacity: 0 }}
              animate={{
                      opacity: [0.2, 0.6, 0.2],
                      scale: [0.8, 1.1, 0.8],
                      r: [1.2, 2, 1.2]
              }}
              transition={{
                      duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
                      delay: i * 0.2
                    }}
                  />
                );
              })}
          </svg>
          </div>
        </div>
      </motion.div>

      {/* Scroll Timeline on the Left - Only visible when ProductPage is in view */}
      <motion.div 
        style={{ opacity: graphicOpacity }}
        className="fixed left-0 top-1/2 -translate-y-1/2 w-1/2 h-screen flex items-center justify-center z-10 hidden lg:flex"
      >
        <div className="relative w-full max-w-md mx-8">
          {/* Timeline Line */}
          <div className="absolute left-8 top-0 w-0.5 h-full bg-gradient-to-b from-blue-500 via-purple-500 to-cyan-500"></div>
          
          {/* Timeline Items */}
          <div className="space-y-16">
            {/* Item 1 */}
            <motion.div
              style={{
                opacity: useTransform(timelineProgress, [0, 0.3, 0.7, 1], [0.3, 1, 1, 0.3]),
                x: useTransform(timelineProgress, [0, 0.3, 0.7, 1], [-50, 0, 0, 50]),
                scale: useTransform(timelineProgress, [0, 0.3, 0.7, 1], [0.8, 1, 1, 0.8])
              }}
              className="relative flex items-center"
            >
              <motion.div 
                className="absolute left-6 w-4 h-4 bg-blue-500 rounded-full border-4 border-white"
                style={{
                  scale: useTransform(timelineProgress, [0, 0.3, 0.7, 1], [0.5, 1, 1, 0.5]),
                  boxShadow: useTransform(timelineProgress, [0, 0.3, 0.7, 1], [
                    "0 0 0px rgba(59, 130, 246, 0)",
                    "0 0 20px rgba(59, 130, 246, 0.8)",
                    "0 0 20px rgba(59, 130, 246, 0.8)",
                    "0 0 0px rgba(59, 130, 246, 0)"
                  ])
                }}
              />
              <div className="ml-16">
                <h3 className="text-xl font-bold text-black mb-2">Intelligent Workflow Automation</h3>
                <p className="text-gray-700 text-sm">Automate tasks, boost speed, and let AI handle the heavy lifting.</p>
              </div>
            </motion.div>

            {/* Item 2 */}
            <motion.div
              style={{
                opacity: useTransform(timelineProgress, [0.5, 0.8, 1.2, 1.5], [0.3, 1, 1, 0.3]),
                x: useTransform(timelineProgress, [0.5, 0.8, 1.2, 1.5], [-50, 0, 0, 50]),
                scale: useTransform(timelineProgress, [0.5, 0.8, 1.2, 1.5], [0.8, 1, 1, 0.8])
              }}
              className="relative flex items-center"
            >
              <motion.div 
                className="absolute left-6 w-4 h-4 bg-purple-500 rounded-full border-4 border-white"
                style={{
                  scale: useTransform(timelineProgress, [0.5, 0.8, 1.2, 1.5], [0.5, 1, 1, 0.5]),
                  boxShadow: useTransform(timelineProgress, [0.5, 0.8, 1.2, 1.5], [
                    "0 0 0px rgba(147, 51, 234, 0)",
                    "0 0 20px rgba(147, 51, 234, 0.8)",
                    "0 0 20px rgba(147, 51, 234, 0.8)",
                    "0 0 0px rgba(147, 51, 234, 0)"
                  ])
                }}
              />
              <div className="ml-16">
                <h3 className="text-xl font-bold text-black mb-2">Adaptive AI Integration</h3>
                <p className="text-gray-700 text-sm">AI that learns, evolves, and fits your workflow perfectly.</p>
              </div>
            </motion.div>

            {/* Item 3 */}
            <motion.div
              style={{
                opacity: useTransform(timelineProgress, [1, 1.3, 1.7, 2], [0.3, 1, 1, 0.3]),
                x: useTransform(timelineProgress, [1, 1.3, 1.7, 2], [-50, 0, 0, 50]),
                scale: useTransform(timelineProgress, [1, 1.3, 1.7, 2], [0.8, 1, 1, 0.8])
              }}
              className="relative flex items-center"
            >
              <motion.div 
                className="absolute left-6 w-4 h-4 bg-cyan-500 rounded-full border-4 border-white"
                style={{
                  scale: useTransform(timelineProgress, [1, 1.3, 1.7, 2], [0.5, 1, 1, 0.5]),
                  boxShadow: useTransform(timelineProgress, [1, 1.3, 1.7, 2], [
                    "0 0 0px rgba(6, 182, 212, 0)",
                    "0 0 20px rgba(6, 182, 212, 0.8)",
                    "0 0 20px rgba(6, 182, 212, 0.8)",
                    "0 0 0px rgba(6, 182, 212, 0)"
                  ])
                }}
              />
              <div className="ml-16">
                <h3 className="text-xl font-bold text-black mb-2">Scalable Cloud Infrastructure</h3>
                <p className="text-gray-700 text-sm">Fast, scalable, and always online — built for performance.</p>
              </div>
            </motion.div>

            {/* Item 4 */}
            <motion.div
              style={{
                opacity: useTransform(timelineProgress, [1.5, 1.8, 2.2, 2.5], [0.3, 1, 1, 0.3]),
                x: useTransform(timelineProgress, [1.5, 1.8, 2.2, 2.5], [-50, 0, 0, 50]),
                scale: useTransform(timelineProgress, [1.5, 1.8, 2.2, 2.5], [0.8, 1, 1, 0.8])
              }}
              className="relative flex items-center"
            >
              <motion.div 
                className="absolute left-6 w-4 h-4 bg-green-500 rounded-full border-4 border-white"
                style={{
                  scale: useTransform(timelineProgress, [1.5, 1.8, 2.2, 2.5], [0.5, 1, 1, 0.5]),
                  boxShadow: useTransform(timelineProgress, [1.5, 1.8, 2.2, 2.5], [
                    "0 0 0px rgba(34, 197, 94, 0)",
                    "0 0 20px rgba(34, 197, 94, 0.8)",
                    "0 0 20px rgba(34, 197, 94, 0.8)",
                    "0 0 0px rgba(34, 197, 94, 0)"
                  ])
                }}
              />
              <div className="ml-16">
                <h3 className="text-xl font-bold text-black mb-2">Secure & Transparent Systems</h3>
                <p className="text-gray-700 text-sm">Your data, encrypted and protected at every step.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Mobile Timeline - Only visible on small screens */}
      <div className="lg:hidden relative z-20">
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">Key Features</h2>
            <p className="text-gray-600 text-lg">Discover what makes our AI automation powerful</p>
          </div>
          
          {/* Mobile Timeline */}
          <div className="relative max-w-md mx-auto">
            {/* Timeline Line */}
            <div className="absolute left-8 top-0 w-0.5 h-full bg-gradient-to-b from-blue-500 via-purple-500 to-cyan-500"></div>
            
            {/* Timeline Items */}
            <div className="space-y-16">
              {/* Item 1 */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                viewport={{ once: true, margin: "-100px" }}
                className="relative flex items-center"
              >
                <motion.div 
                  className="absolute left-6 w-4 h-4 bg-blue-500 rounded-full border-4 border-white shadow-lg"
                  initial={{ scale: 0.5 }}
                  whileInView={{ scale: 1 }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                  viewport={{ once: true }}
                />
                <div className="ml-16">
                  <h3 className="text-xl font-bold text-black mb-2">Intelligent Workflow Automation</h3>
                  <p className="text-gray-700 text-sm">Automate tasks, boost speed, and let AI handle the heavy lifting.</p>
                </div>
              </motion.div>

              {/* Item 2 */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
                viewport={{ once: true, margin: "-100px" }}
                className="relative flex items-center"
              >
                <motion.div 
                  className="absolute left-6 w-4 h-4 bg-purple-500 rounded-full border-4 border-white shadow-lg"
                  initial={{ scale: 0.5 }}
                  whileInView={{ scale: 1 }}
                  transition={{ duration: 0.4, ease: "easeOut", delay: 0.1 }}
                  viewport={{ once: true }}
                />
                <div className="ml-16">
                  <h3 className="text-xl font-bold text-black mb-2">Adaptive AI Integration</h3>
                  <p className="text-gray-700 text-sm">AI that learns, evolves, and fits your workflow perfectly.</p>
                </div>
              </motion.div>

              {/* Item 3 */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
                viewport={{ once: true, margin: "-100px" }}
                className="relative flex items-center"
              >
                <motion.div 
                  className="absolute left-6 w-4 h-4 bg-cyan-500 rounded-full border-4 border-white shadow-lg"
                  initial={{ scale: 0.5 }}
                  whileInView={{ scale: 1 }}
                  transition={{ duration: 0.4, ease: "easeOut", delay: 0.2 }}
                  viewport={{ once: true }}
                />
                <div className="ml-16">
                  <h3 className="text-xl font-bold text-black mb-2">Scalable Cloud Infrastructure</h3>
                  <p className="text-gray-700 text-sm">Fast, scalable, and always online — built for performance.</p>
                </div>
              </motion.div>

              {/* Item 4 */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, ease: "easeOut", delay: 0.3 }}
                viewport={{ once: true, margin: "-100px" }}
                className="relative flex items-center"
              >
                <motion.div 
                  className="absolute left-6 w-4 h-4 bg-green-500 rounded-full border-4 border-white shadow-lg"
                  initial={{ scale: 0.5 }}
                  whileInView={{ scale: 1 }}
                  transition={{ duration: 0.4, ease: "easeOut", delay: 0.3 }}
                  viewport={{ once: true }}
                />
                <div className="ml-16">
                  <h3 className="text-xl font-bold text-black mb-2">Secure & Transparent Systems</h3>
                  <p className="text-gray-700 text-sm">Your data, encrypted and protected at every step.</p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Gradient Separation Line */}
      <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-b from-transparent via-white to-oklch(0.23 0.002 264)"></div>
    </section>
  );
};

export default FeaturesPage;
